import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import LoginScreen from './screens/LoginScreen';
import PublicHomePage from './screens/PublicHomePage';
import PrivacyPolicyPage from './screens/PrivacyPolicyPage';
import TermsOfUsePage from './screens/TermsOfUsePage';
import MainLayout from './screens/MainLayout';
import HomeScreen from './screens/DashBoard';
import MessageSchedule from './screens/MessageSchedule';
import LabelPage from './screens/LabelScreen';
import CRMPage from './screens/CRMPage';
import OpportunityDossierPage from './screens/OpportunityDossierPage';
import SectorsPage from './screens/SectorsPage';
import GoogleAdsLeadsPage from './screens/GoogleAdsLeadsPage';
import AttendancePage from './screens/AttendancePage';
import ProfilePage from './screens/ProfilePage';
import NotFoundPage from './screens/NotFoundPage';
import Chat from './screens/Chat';
import AIAgentPage from './screens/AIAgentPage';
import FollowUpPage from './screens/FollowUpPage';
import GroupsPage from './screens/GroupsPage';
import ContactsPage from './screens/ContactsPage';
import BroadcastPage from './screens/BroadcastPage';
import EmailPage from './screens/EmailPage';
import AgendaPage from './screens/AgendaPage';
import SubscriptionPage from './screens/SubscriptionPage';
import SessionService from './services/SessionService';
import { getSubscription } from './services/SubscriptionService';
import { LoadingSpinner } from './components/LoadingSpinner';
import {
  IntelligenceCenterPage,
  MindPage,
  TodayPage,
  RegisterPage,
  OnboardingPage,
  isMendsVisionEnabled,
} from './mendsAi';

import { SectorProvider } from './contexts/SectorContext';
import { ModuleProvider, useModules } from './contexts/ModuleContext';
import { UserModules } from './services/ModuleService';
import { homePath } from './utils/homePath';

/** Rotas legadas → abas da Central (preserva ?id= nas aprovações). */
const RedirectToCentralTab: React.FC<{ tab: string }> = ({ tab }) => {
  const [params] = useSearchParams();
  const next = new URLSearchParams();
  next.set('tab', tab);
  const id = params.get('id');
  if (id) next.set('id', id);
  return <Navigate to={`/central-inteligencia?${next.toString()}`} replace />;
};

const SubscriptionGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const [status, setStatus] = useState<'checking' | 'allowed' | 'pending'>('checking');

  useEffect(() => {
    const check = async () => {
      const token = SessionService.getToken();
      if (!token || SessionService.isTokenExpired(token)) {
        setStatus('allowed');
        return;
      }
      try {
        const res = await getSubscription();
        const subStatus = res.subscription?.status;
        if (subStatus === 'pending_payment') {
          SessionService.setSession('@app/subscription_pending', true);
          setStatus('pending');
        } else {
          localStorage.removeItem('@app/subscription_pending');
          setStatus('allowed');
        }
      } catch {
        setStatus('allowed');
      }
    };
    check();
  }, []);

  if (status === 'checking') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-secondary, #0a0a0a)',
        gap: 12
      }}>
        <LoadingSpinner size="lg" color="primary" />
        <span style={{ color: 'var(--text-muted, #64748b)', fontSize: 14 }}>Conferindo sua assinatura…</span>
      </div>
    );
  }
  if (status === 'pending') {
    return <Navigate to="/subscription" replace />;
  }
  return children;
};

const ModuleRoute: React.FC<{ moduleKey: keyof UserModules; children: React.ReactElement }> = ({ moduleKey, children }) => {
  const { modules, loading } = useModules();
  if (loading) return null;
  if (!modules[moduleKey]) return <Navigate to={homePath()} replace />;
  return children;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<LoginScreen />} />
      <Route path="/home" element={<PublicHomePage />} />
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfUsePage />} />
      <Route path="/subscription" element={<SubscriptionPage />} />

      <Route element={
        <SubscriptionGuard>
          <SectorProvider>
            <ModuleProvider>
              <MainLayout />
            </ModuleProvider>
          </SectorProvider>
        </SubscriptionGuard>
      }>
        <Route path="/dashboard" element={<HomeScreen />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/follow-up" element={<FollowUpPage />} />
        <Route path="/labels" element={<LabelPage />} />
        <Route path="/crm" element={<ModuleRoute moduleKey="crm"><CRMPage /></ModuleRoute>} />
        <Route path="/crm/oportunidade/:cardId" element={<ModuleRoute moduleKey="crm"><div className="page-container"><OpportunityDossierPage /></div></ModuleRoute>} />
        <Route path="/schedule" element={<ModuleRoute moduleKey="schedule"><MessageSchedule /></ModuleRoute>} />
        <Route path="/agenda" element={<ModuleRoute moduleKey="calendar"><div className="page-container"><AgendaPage /></div></ModuleRoute>} />
        <Route path="/sectors" element={<SectorsPage />} />
        <Route path="/google-ads-leads" element={<div className="page-container"><GoogleAdsLeadsPage /></div>} />
        <Route path="/prazo-atendimento" element={<div className="page-container"><AttendancePage /></div>} />
        <Route path="/profile" element={<div className="page-container"><ProfilePage /></div>} />
        <Route path="/ai-agent" element={<AIAgentPage />} />
        <Route path="/groups" element={<div className="page-container"><GroupsPage /></div>} />
        <Route path="/contacts" element={<div className="page-container"><ContactsPage /></div>} />
        <Route path="/broadcast" element={<ModuleRoute moduleKey="campaign"><div className="page-container"><BroadcastPage /></div></ModuleRoute>} />
        <Route path="/email" element={<ModuleRoute moduleKey="email"><div className="page-container"><EmailPage /></div></ModuleRoute>} />
        {isMendsVisionEnabled() && (
          <>
            <Route path="/mente" element={<MindPage />} />
            <Route path="/hoje" element={<div className="page-container"><TodayPage /></div>} />
            <Route path="/registrar" element={<div className="page-container"><RegisterPage /></div>} />
            <Route path="/comecar" element={<div className="page-container"><OnboardingPage /></div>} />
            <Route path="/oportunidades" element={<RedirectToCentralTab tab="oportunidades" />} />
            <Route path="/aprovacoes" element={<RedirectToCentralTab tab="aprovacoes" />} />
            <Route path="/central-inteligencia" element={<div className="page-container"><IntelligenceCenterPage /></div>} />
          </>
        )}
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;  